import { Link } from "react-router-dom";
import BadgeTier from "./BadgeTier";
import StarRating from "./StarRating";
import { MapPin, Users, BadgeCheck, Crown, Ban, Trash2, RotateCcw } from "lucide-react";
import { BACKEND_URL, NEXUS_LOGO } from "../lib/api";

function toAbsolute(url) {
  if (!url) return url;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

const NICHE_COLORS = {
  Fashion: "bg-pink-500/10 text-pink-300 border-pink-500/30",
  Beauty: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  Fitness: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Food: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Travel: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  Tech: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  Gaming: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  Lifestyle: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

function nicheCls(n) {
  return NICHE_COLORS[n] || "bg-white/5 text-zinc-300 border-white/10";
}

function formatFollowers(n = 0) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function CreatorCard({ creator, index = 0 }) {
  const verificationCount = Object.values(creator.verifications || {}).filter(Boolean).length;
  const uploaded =
    toAbsolute(creator.cover_url) ||
    toAbsolute(creator.avatar_url);
  const cover = uploaded || NEXUS_LOGO;
  const isFallback = !uploaded;

  return (
    <Link
      to={`/creators/${creator.user_id}`}
      className="group block rounded-2xl overflow-hidden hairline bg-[#0C0C10] card-hover beam-hover"
      data-testid={`creator-card-${creator.user_id}`}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={cover}
          alt={creator.name}
          className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${
            isFallback
              ? "object-contain p-8 bg-gradient-to-br from-nexus-pink/10 via-nexus-purple/10 to-nexus-blue/10 opacity-80"
              : "object-cover"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C10] via-[#0C0C10]/40 to-transparent" />
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          {creator.brand_ready_score >= 75 && (
            <span
              title={`Brand Ready · ${creator.brand_ready_score}/100`}
              className="h-6 px-2 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
              data-testid="creator-card-brand-ready"
            >
              ★ {creator.brand_ready_score}
            </span>
          )}
          {creator.hof_badges?.length > 0 && (
            <span
              title={`${creator.hof_badges.length} Hall of Fame badge${creator.hof_badges.length === 1 ? "" : "s"}`}
              className="h-6 w-6 rounded-full nexus-gradient flex items-center justify-center shadow-[0_0_15px_rgba(157,78,221,0.5)]"
              data-testid="creator-card-hof-chip"
            >
              <Crown className="h-3 w-3 text-white" />
            </span>
          )}
          <BadgeTier tier={creator.badge_tier} size="sm" />
          {creator.__onSave && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); creator.__onSave(creator.user_id); }}
              data-testid={`save-creator-${creator.user_id}`}
              className="ml-1 h-6 w-6 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              title="Save creator"
            >
              ★
            </button>
          )}
        </div>
        {(creator.agency_badges || []).includes("iia") && (
          <span data-testid={`iia-badge-${creator.user_id}`} title="Represented by International Idols Agency" className="absolute bottom-3 left-3 h-6 px-2 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-200 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> IIA Talent</span>
        )}
        {creator.accepting_offers === false && !creator.is_suspended && (
          <span
            data-testid={`not-accepting-badge-${creator.user_id}`}
            className="absolute top-3 left-3 h-6 px-2 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[10px] uppercase tracking-widest font-bold flex items-center"
          >
            Not taking offers
          </span>
        )}
        {creator.is_suspended && (
          <span
            data-testid={`suspended-badge-${creator.user_id}`}
            className="absolute top-3 left-3 h-6 px-2 rounded-full bg-red-500/20 border border-red-500/40 text-red-200 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
          >
            <Ban className="h-3 w-3" /> Suspended
          </span>
        )}
        {creator.__admin && (
          <div
            className="absolute bottom-3 left-3 right-3 flex items-center gap-2"
            data-testid={`admin-actions-${creator.user_id}`}
          >
            {creator.is_suspended ? (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); creator.__admin.onUnsuspend(creator); }}
                data-testid={`admin-unsuspend-${creator.user_id}`}
                className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/30 text-[11px] font-semibold uppercase tracking-widest transition-colors"
                title="Reinstate account"
              >
                <RotateCcw className="h-3 w-3" /> Reinstate
              </button>
            ) : (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); creator.__admin.onSuspend(creator); }}
                data-testid={`admin-suspend-${creator.user_id}`}
                className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-100 hover:bg-amber-500/30 text-[11px] font-semibold uppercase tracking-widest transition-colors"
                title="Suspend this account"
              >
                <Ban className="h-3 w-3" /> Suspend
              </button>
            )}
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); creator.__admin.onDelete(creator); }}
              data-testid={`admin-delete-${creator.user_id}`}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 transition-colors"
              title="Delete this user permanently"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <h3 className="font-display font-bold text-lg truncate">{creator.name}</h3>
              {verificationCount >= 4 && (
                <BadgeCheck className="h-4 w-4 text-nexus-blue shrink-0" />
              )}
            </div>
            <p className="text-sm text-zinc-400 line-clamp-1">
              {creator.tagline || creator.bio || "Content creator on Nexus"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {(creator.niches || []).slice(0, 3).map((n) => (
            <span
              key={n}
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${nicheCls(n)}`}
            >
              {n}
            </span>
          ))}
          {(creator.growth_badges || []).slice(0, 2).map((b) => (
            <span
              key={b}
              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-nexus-blue/30 bg-nexus-blue/10 text-nexus-blue"
              data-testid={`growth-badge-${b}`}
            >
              {b.replace(/_/g, " ")}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <StarRating value={creator.star_rating || 0} count={creator.review_count || 0} />
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {formatFollowers(creator.follower_count)}
            </span>
            {creator.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {creator.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
